import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { setShowToast } from '../toast/toastSlice';
import { ETypeToast } from '../../app/enum';
import { convertValueToNumber, getLabelError } from '../../helpers/funcs';
import { surveyMasterApi } from '../../api/surveyMaster';
import { TGetSurveyResult, TSurvey, TSurveyMaster, TSurveyResult } from '../../types/survey/survey';
import {
  DEFAULT_ORDER_BY,
  DEFAULT_PAGE,
  DEFAULT_PER_PAGE,
  IndexedObject,
  ORDER_DIRECTION_DESC,
  TActionParams,
  TOptionsQuery,
} from '../../types/common';
import { surveyApi } from '../../api/surveyApi';
import { TSetListAction } from '../../types/types';
import { t } from 'i18next';
import { translations } from '../../locales/translations';
import { TEmployeeManagement } from '../../types/clientManagement/clientManagement';
import { TSurveyProgress } from 'types/surveyProgress/surveyProgress';

export type SurveyState = {
  loading: boolean;
  listSurveyMaster: Array<TSurveyMaster>;
  listSurvey: Array<TSurvey>;
  listEmployeeNotJoin: TEmployeeManagement[];
  surveyDetail: TSurvey;
  surveyGroupDetail: TSurvey[];
  total: number;
  page: number;
  totalPage: number;
  currentLoad: boolean;
  messageErr: string;
  groupMultiSelected: string[];
  surveyResult: TSurveyResult;
};

const initialState: SurveyState = {
  loading: false,
  listSurveyMaster: [],
  listSurvey: [],
  listEmployeeNotJoin: [],
  surveyDetail: {},
  surveyGroupDetail: [],
  total: 0,
  page: 1,
  totalPage: 1,
  currentLoad: false,
  messageErr: '',
  groupMultiSelected: ['All'],
  surveyResult: {},
};

export const getListSurveyMaster = createAsyncThunk(
  'survey/getListSurveyMaster',
  async (params: any, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await surveyMasterApi
      .getListSurveyMaster()
      .then((response) => {
        thunkAPI.dispatch(
          setListSurveyMaster({
            surveyMasters: response.data.data,
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const getListSurvey = createAsyncThunk(
  'survey/getListSurvey',
  async (params: TOptionsQuery<TSurvey>, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    const { loadMore, ...rest } = params;
    const newParams: TOptionsQuery<TSurvey> = {
      ...rest,
      orderBy: rest.orderBy ?? DEFAULT_ORDER_BY,
      orderDirection: rest.orderDirection ?? ORDER_DIRECTION_DESC,
      page: rest.page ?? DEFAULT_PAGE,
      perPage: rest.perPage ?? DEFAULT_PER_PAGE,
    };
    await surveyApi
      .getListSurvey(newParams)
      .then((response) => {
        thunkAPI.dispatch(
          setListSurvey({
            type: loadMore ? 'loadMore' : 'getList',
            surveys: response.data.data,
            total: response.data._metadata.pagination.total,
            page: response.data._metadata.pagination.page,
            totalPage: response.data._metadata.pagination.totalPage,
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });

    thunkAPI.dispatch(setLoading(false));
  },
);

export const registerSurvey = createAsyncThunk(
  'survey/registerSurvey',
  async (actionParams: TActionParams<TSurvey>, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await surveyApi
      .registerSurvey(actionParams.data)
      .then(() => {
        thunkAPI.dispatch(resetListSurvey());
        thunkAPI.dispatch(setPage(1));
        thunkAPI.dispatch(setCurrentLoad(true));
        thunkAPI.dispatch(resetErrMsg());
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.REGISTER_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
        actionParams.funct && actionParams.funct();
      })
      .catch((response) => {
        if (response.response.data.statusCode === 111230) {
          thunkAPI.dispatch(setErrMsg(response.response.data));
          thunkAPI.dispatch(setLoading(false));
        }
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const updateSurvey = createAsyncThunk(
  'survey/updateSurvey',
  async (params: { id: string; data: TActionParams<TSurvey> }, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await surveyApi
      .updateSurvey({ id: params.id, data: params.data.data })
      .then((response: any) => {
        thunkAPI.dispatch(
          setListSurvey({
            type: 'edit',
            survey: { ...params.data.data, _id: response.data.data._id },
          }),
        );
        thunkAPI.dispatch(getListSurvey({}));
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.UPDATE_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
        params.data.funct && params.data.funct();
      })
      .catch((response) => {
        if (response.response.data.statusCode === 111230) {
          thunkAPI.dispatch(setErrMsg(response.response.data));
          thunkAPI.dispatch(setLoading(false));
        }
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const deleteSurvey = createAsyncThunk(
  'survey/deleteSurvey',
  async (survey: TSurvey, thunkAPI) => {
    thunkAPI.dispatch(setLoading(true));
    await surveyApi
      .deleteImplement(String(survey._id))
      .then(() => {
        thunkAPI.dispatch(setListSurvey({ type: 'delete', survey: survey }));
        thunkAPI.dispatch(
          setShowToast({
            label: t(translations.ID_MASTER.DELETE_SUCCESS),
            type: ETypeToast.Success,
          }),
        );
      })
      .catch((response) => {
        thunkAPI.dispatch(
          setShowToast({
            label: getLabelError(response.response.data.statusCode),
            type: ETypeToast.Error,
          }),
        );
        thunkAPI.dispatch(setLoading(false));
      });
    thunkAPI.dispatch(setLoading(false));
  },
);

export const getSurveyByFacility = createAsyncThunk(
  'survey/getSurveyByFacility',
  async (id: string) => {
    const response = await surveyApi.getSurveyByFacility(id);
    return response.data.data;
  },
);

export const getSurveyByGroup = createAsyncThunk('survey/getSurveyByGroup', async (params: any) => {
  const response = await surveyApi.getSurveyByGroup(params);
  const data = response.data.data;
  if (params.selected && !params.selected.includes('All')) {
    return data.filter((obj) => params.selected.includes(obj.facilityId));
  }
  return data;
});

export const getListEmployeeNotJoinSurvey = createAsyncThunk(
  'survey/getListEmployeeNotJoin',
  async (id: string) => {
    const response = await surveyApi.getListEmployeeNotJoinSurvey(id);
    return response.data.data.employees;
  },
);

export const getSurveyResult = createAsyncThunk(
  'survey/getSurveyResult',
  async (data: { employeeId: string; surveyId: string }) => {
    const params: TOptionsQuery<TGetSurveyResult> = {
      ...data,
      orderBy: DEFAULT_ORDER_BY,
      orderDirection: ORDER_DIRECTION_DESC,
      page: DEFAULT_PAGE,
      perPage: DEFAULT_PER_PAGE,
    };
    const response = await surveyApi.getSurveyResult(params);
    const surveyProgress = (response.data?.data?.[0] as TSurveyProgress) ?? {};
    return {
      ...(surveyProgress?.surveyResult ?? {}),
      employee: response.data?._metadata?.employee ?? {},
      survey: surveyProgress?.survey ?? {},
    };
  },
);

const slice = createSlice({
  name: 'survey',
  initialState,
  reducers: {
    setListSurveyMaster: (
      state,
      action: PayloadAction<{
        surveyMasters?: TSurveyMaster[];
      }>,
    ) => {
      const { surveyMasters } = action.payload;

      if (!surveyMasters) return;

      state.listSurveyMaster = surveyMasters;
    },
    setListSurvey: (
      state,
      action: PayloadAction<{
        type: TSetListAction;
        survey?: TSurvey;
        surveys?: TSurvey[];
        page?: number;
        total?: number;
        totalPage?: number;
      }>,
    ) => {
      const { type, survey, surveys, total, page, totalPage } = action.payload;
      if (survey) {
        switch (type) {
          case 'create':
            state.listSurvey = [{ ...survey, isActive: true }, ...state.listSurvey];
            break;
          case 'edit':
            state.listSurvey = state.listSurvey.map((m: TSurvey) =>
              m._id === survey._id ? { ...survey, isActive: true } : m,
            );
            break;
          case 'delete':
            state.listSurvey = state.listSurvey.filter((m: TSurvey) => m._id !== survey._id);
            state.total -= 1;
            break;
          case 'stop':
            state.listSurvey = state.listSurvey.map((m: TSurvey) =>
              m._id === survey._id ? { ...survey, isActive: !survey.isActive } : m,
            );
        }
      }
      if (surveys) {
        switch (type) {
          case 'getList':
            state.listSurvey = surveys;
            break;
          case 'loadMore':
            state.listSurvey = [...state.listSurvey, ...surveys];
            break;
        }

        state.page = page ?? DEFAULT_PAGE;
        state.total = convertValueToNumber(total);
        state.totalPage = convertValueToNumber(totalPage);
      }
    },
    resetListSurvey: (state) => {
      state.listSurvey = [];
    },
    setGroupMultiSelected: (state, action: PayloadAction<Array<string>>) => {
      state.groupMultiSelected = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setCurrentLoad: (state, action: PayloadAction<boolean>) => {
      state.currentLoad = action.payload;
    },
    setErrMsg: (state, action: PayloadAction<IndexedObject>) => {
      const { message, data } = action.payload ?? {};
      if (message && data) {
        state.messageErr = data.employeeNames?.length
          ? message + '<br/>' + data.employeeNames.join('<br/>')
          : message;
      } else {
        state.messageErr = 'この期間中に調査が既に存在しています。';
      }
    },
    resetErrMsg: (state) => {
      state.messageErr = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSurveyByFacility.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getSurveyByFacility.fulfilled, (state, action: PayloadAction<TSurvey>) => {
      state.loading = false;
      state.surveyDetail = action.payload;
    });

    builder.addCase(getSurveyByGroup.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getSurveyByGroup.fulfilled, (state, action: PayloadAction<TSurvey[]>) => {
      state.loading = false;
      state.surveyGroupDetail = action.payload;
    });
    builder.addCase(getListEmployeeNotJoinSurvey.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getListEmployeeNotJoinSurvey.fulfilled,
      (state, action: PayloadAction<TEmployeeManagement[]>) => {
        state.loading = false;
        state.listEmployeeNotJoin = action.payload;
      },
    );

    builder.addCase(getSurveyResult.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getSurveyResult.fulfilled, (state, action: PayloadAction<TSurveyResult>) => {
      state.loading = false;
      state.surveyResult = action.payload;
    });
  },
});

export const { actions, reducer: SurveyReducer } = slice;
export const {
  setListSurveyMaster,
  setLoading,
  setGroupMultiSelected,
  setListSurvey,
  resetListSurvey,
  setPage,
  setCurrentLoad,
  setErrMsg,
  resetErrMsg,
} = actions;
export default SurveyReducer;
